import React from "react";
import { C } from "../../../constants/design";
import type { MatchState } from "../types/leagueDuel";
import { matchResult } from "../engine/simulation";
import { useI18n } from "../../../i18n/I18nContext";

// ─── Écran de fin de duel ─────────────────────────────────────────────────────

const COIN_ICON = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/CoinIcon.png";
const GOLD_WIN  = 50;
const GOLD_LOSS = 25;

const OVERLAY: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 200,
  background: "rgba(10,7,8,0.70)",
  backdropFilter: "blur(42px)",
  WebkitBackdropFilter: "blur(42px)",
};

export function LeagueDuelResult({
  state,
  outcome,
  onContinue,
}: {
  state: MatchState;
  outcome?: {
    reputationDelta: number;
    rankBefore: string;
    rankAfter: string;
    xpGain?: number;
  } | null;
  onContinue: () => void;
}) {
  const { t } = useI18n();
  const result      = matchResult(state);
  const isWin       = result === "win";
  const goldEarned  = isWin ? GOLD_WIN : GOLD_LOSS;
  const accentColor = isWin ? "#48FF85" : C.live;
  const label       = isWin ? t("leagueDuel.resultWin") : t("leagueDuel.resultLoss");

  return (
    <div style={{ ...OVERLAY, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <style>{`
        @keyframes duelResultPop {
          from { opacity: 0; transform: scale(0.72) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes duelGoldPop {
          from { opacity: 0; transform: scale(0.6) translateY(10px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0,
          animation: "duelResultPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
          width: "100%",
          maxWidth: 340,
          padding: "0 24px",
        }}
      >
        {/* Victory / Defeat */}
        <div
          style={{
            fontSize: 42,
            fontWeight: 900,
            color: accentColor,
            letterSpacing: "-0.02em",
            textAlign: "center",
            marginBottom: 8,
            textShadow: `0 0 40px ${accentColor}55`,
          }}
        >
          {label}
        </div>

        {/* Score */}
        <div
          style={{
            fontFamily: "monospace",
            fontSize: 36,
            fontWeight: 800,
            color: "#fff",
            marginBottom: 6,
          }}
        >
          {state.homeScore} – {state.awayScore}
        </div>

        {/* Names */}
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 28, textAlign: "center" }}>
          {state.home.name} vs {state.away.name}
          {state.goldenGoal && (
            <span style={{ marginLeft: 8, color: C.gold, fontWeight: 700 }}>{t("leagueDuel.goldenGoal")}</span>
          )}
        </div>

        {/* Reputation delta */}
        {outcome && (
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: outcome.reputationDelta >= 0 ? "#48FF85" : C.live,
              marginBottom: 28,
            }}
          >
            {outcome.reputationDelta >= 0 ? `+${outcome.reputationDelta}` : outcome.reputationDelta} {t("leagueDuel.reputation")}
          </div>
        )}

        {/* New rank */}
        {outcome?.rankAfter && outcome.rankAfter !== outcome.rankBefore && (
          <div style={{ fontSize: 13, color: C.gold, fontWeight: 700, marginTop: -20, marginBottom: 28 }}>
            {t("leagueDuel.newRankLabel", outcome.rankAfter)}
          </div>
        )}

        {/* Rewards row: Gold + XP */}
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
            animation: "duelGoldPop 0.32s 120ms cubic-bezier(0.34,1.56,0.64,1) both",
            marginBottom: 40,
          }}
        >
          {/* Gold box */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  inset: -10,
                  borderRadius: "50%",
                  background: "radial-gradient(ellipse at center, #FFB700 0%, transparent 70%)",
                  opacity: 0.28,
                }}
              />
              <div
                style={{
                  position: "relative",
                  width: 82,
                  height: 82,
                  borderRadius: 12,
                  border: "1px solid #FFE677",
                  background: "rgba(255,182,0,0.06)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <img src={COIN_ICON} alt="" style={{ width: 44, height: 44, objectFit: "contain" }} />
              </div>
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", marginTop: 10 }}>+{goldEarned}</div>
          </div>

          {/* XP box */}
          {outcome?.xpGain != null && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div
                style={{
                  width: 82,
                  height: 82,
                  borderRadius: 12,
                  border: "1px solid #7B61FF",
                  background: "rgba(123,97,255,0.08)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <span style={{ fontSize: 20, fontWeight: 900, color: "#A78BFA", letterSpacing: "0.04em" }}>XP</span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", marginTop: 10 }}>+{outcome.xpGain}</div>
            </div>
          )}
        </div>
      </div>

      {/* CTA sticky */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "48px 24px 28px",
          background: "linear-gradient(to bottom, rgba(10,7,8,0) 0%, rgba(10,7,8,0.9) 40%, rgba(10,7,8,1) 100%)",
          pointerEvents: "none",
        }}
      >
        <button
          onClick={onContinue}
          style={{
            width: "100%",
            maxWidth: 460,
            display: "block",
            margin: "0 auto",
            background: "#48FF85",
            color: "#05140A",
            border: "none",
            borderRadius: 14,
            padding: "16px 0",
            fontSize: 15,
            fontWeight: 800,
            cursor: "pointer",
            pointerEvents: "auto",
          }}
        >
          {t("leagueDuel.continueButton")}
        </button>
      </div>
    </div>
  );
}
